import React, { useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import { addRewardVoucher } from "../../Redux/Actions/rewardsAction";
import { useNavigate, Link } from "react-router-dom";
import "./addReward.css";

const CATEGORY_OPTIONS = [
  "Other's",
  "Electronics",
  "Luxury",
  "Travel",
  "Tech",
  "Lifestyle",
  "Food",
];

const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AddRewards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { countryList = { data: [] } } = useSelector((state) => state.country || {});

  const [activeSection, setActiveSection] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState({ image: false, brochure: false });

  const imageInputRef = useRef(null);
  const brochureInputRef = useRef(null);

  const [formState, setFormState] = useState({
    voucher: "",
    title: "",
    description: "",
    reward_points: "",
    image: "",
    brochure: "",
    terms_and_conditions: [""],
    how_to_redeem: [""],
    category: "Other's",
    position: "",
    country: "",
  });

  const sections = ["Basic Info", "Details", "Content", "Media"];

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "title" && value.length > MAX_TITLE_LENGTH) return;
    if (name === "description" && value.length > MAX_DESCRIPTION_LENGTH) return;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...formState[field]];
    updated[index] = value;
    setFormState((prev) => ({ ...prev, [field]: updated }));
  };

  const handleAddArrayItem = (field) => {
    setFormState((prev) => ({ ...prev, [field]: [...prev[field], ""] }));
  };

  const handleRemoveArrayItem = (field, index) => {
    if (formState[field].length <= 1) {
      toast.error("At least one item is required.");
      return;
    }
    const updated = [...formState[field]];
    updated.splice(index, 1);
    setFormState((prev) => ({ ...prev, [field]: updated }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload an image in PNG, JPEG, or JPG format only.");
      event.target.value = "";
      return;
    }

    setUploading((prev) => ({ ...prev, image: true }));
    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, image: uploadedUrl }));
        setImagePreview(uploadedUrl);
        toast.success("Image uploaded successfully");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, image: false }));
    }
  };

  const handleBrochureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file only.");
      event.target.value = "";
      return;
    }

    setUploading((prev) => ({ ...prev, brochure: true }));
    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, brochure: uploadedUrl }));
        toast.success("Brochure uploaded successfully");
      }
    } catch {
      toast.error("PDF upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, brochure: false }));
    }
  };

  const handleReset = () => {
    setFormState({
      voucher: "",
      title: "",
      description: "",
      reward_points: "",
      image: "",
      brochure: "",
      terms_and_conditions: [""],
      how_to_redeem: [""],
      category: "Other's",
      position: "",
      country: "",
    });
    setImagePreview(null);
    setActiveSection(0);
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (brochureInputRef.current) brochureInputRef.current.value = "";
  };

  const handleNextSection = () => {
    setActiveSection((s) => Math.min(sections.length - 1, s + 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.country) return toast.error("Country is required");
    if (!formState.voucher.trim()) return toast.error("Voucher code is required");
    if (!formState.title.trim()) return toast.error("Title is required");
    if (formState.title.length > MAX_TITLE_LENGTH) return toast.error(`Title cannot exceed ${MAX_TITLE_LENGTH} characters`);
    if (!formState.description.trim()) return toast.error("Description is required");
    if (formState.description.length > MAX_DESCRIPTION_LENGTH) return toast.error(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
    if (!formState.reward_points) return toast.error("Reward points is required");

    const rewardPointsValue = Number(formState.reward_points);
    if (!Number.isInteger(rewardPointsValue) || rewardPointsValue <= 0) {
      return toast.error("Reward points must be a positive integer");
    }

    if (!formState.category) return toast.error("Category is required");
    if (formState.terms_and_conditions.every((t) => t.trim() === "")) {
      return toast.error("Please add at least one Term & Condition");
    }
    if (formState.how_to_redeem.every((t) => t.trim() === "")) {
      return toast.error("Please add at least one How to Redeem step");
    }
    if (!formState.image) return toast.error("Image is required");

    const payload = {
      ...formState,
      reward_points: rewardPointsValue,
      country_id: formState.country,
      terms_and_conditions: formState.terms_and_conditions.filter((t) => t.trim() !== ""),
      how_to_redeem: formState.how_to_redeem.filter((t) => t.trim() !== ""),
    };

    try {
      const response = await dispatch(addRewardVoucher(payload));
      if (response && (response.statusCode === 200 || response.status === "success")) {
        toast.success("Reward voucher added successfully!");
        handleReset();
        navigate("/rewards");
      } else {
        toast.error(response?.message || "Failed to add reward voucher");
      }
    } catch {
      toast.error("Error submitting reward voucher");
    }
  };

  const CharCountBar = ({ current, max }) => {
    const pct = Math.min((current / max) * 100, 100);
    const color = current >= max ? "#ef4444" : current >= max * 0.8 ? "#f59e0b" : "#10b981";
    return (
      <div className="ar-char-bar-wrap">
        <div className="ar-char-bar">
          <div className="ar-char-bar-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
        <span className="ar-char-count" style={{ color }}>{current}/{max}</span>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="ar-wrapper">
        <div className="ar-header">
          <div className="ar-header-left">
            <div className="ar-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 12V22H4V12" />
                <path d="M22 7H2v5h20V7z" />
                <path d="M12 22V7" />
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
              </svg>
            </div>
            <div>
              <h1 className="ar-title">Add Reward Voucher</h1>
              <p className="ar-subtitle">Fill in the details to create a new reward voucher</p>
            </div>
          </div>
          <Link to="/rewards" className="ar-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 12V22H4V12" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" />
            </svg>
            View Rewards
          </Link>
        </div>

        <div className="ar-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              type="button"
              className={`ar-nav-pill ${activeSection === i ? "ar-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
            >
              <span className="ar-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="ar-form">

          <div className={`ar-section ${activeSection === 0 ? "ar-section--visible" : ""}`}>
            <div className="ar-section-header">
              <span className="ar-section-badge">01</span>
              <h2 className="ar-section-title">Basic Information</h2>
            </div>
            <div className="ar-grid ar-grid-2">
              <div className="ar-field">
                <label className="ar-label">Country <span className="ar-req">*</span></label>
                <select className="ar-select" name="country" value={formState.country} onChange={handleChange}>
                  <option value="">Select country</option>
                  {countryList.data.map((c) => (
                    <option key={c._id} value={c._id}>{c.country_name}</option>
                  ))}
                </select>
              </div>
              <div className="ar-field">
                <label className="ar-label">Voucher Code <span className="ar-req">*</span></label>
                <input className="ar-input" type="text" name="voucher" value={formState.voucher} onChange={handleChange} placeholder="e.g. SAVE50" />
              </div>
              <div className="ar-field">
                <label className="ar-label">Category <span className="ar-req">*</span></label>
                <select className="ar-select" name="category" value={formState.category} onChange={handleChange}>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="ar-field">
                <label className="ar-label">Position <span className="ar-optional">(Optional)</span></label>
                <input
                  className="ar-input"
                  type="number"
                  name="position"
                  value={formState.position}
                  min={1}
                  placeholder="e.g. 1"
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
                    setFormState((prev) => ({ ...prev, position: val }));
                  }}
                />
              </div>
            </div>
          </div>

          <div className={`ar-section ${activeSection === 1 ? "ar-section--visible" : ""}`}>
            <div className="ar-section-header">
              <span className="ar-section-badge">02</span>
              <h2 className="ar-section-title">Voucher Details</h2>
            </div>
            <div className="ar-grid ar-grid-1">
              <div className="ar-field">
                <label className="ar-label">Title <span className="ar-req">*</span></label>
                <input className="ar-input" type="text" name="title" value={formState.title} onChange={handleChange} placeholder="e.g. 50% off Electronics" maxLength={MAX_TITLE_LENGTH} />
                <CharCountBar current={formState.title.length} max={MAX_TITLE_LENGTH} />
              </div>
              <div className="ar-field">
                <label className="ar-label">Description <span className="ar-req">*</span></label>
                <textarea className="ar-textarea" name="description" value={formState.description} onChange={handleChange} placeholder="Briefly describe this reward voucher…" maxLength={MAX_DESCRIPTION_LENGTH} rows={4} />
                <CharCountBar current={formState.description.length} max={MAX_DESCRIPTION_LENGTH} />
              </div>
              <div className="ar-field ar-field--half">
                <label className="ar-label">Reward Points <span className="ar-req">*</span></label>
                <input
                  className="ar-input"
                  type="number"
                  name="reward_points"
                  value={formState.reward_points}
                  onChange={handleChange}
                  placeholder="e.g. 500"
                  min={1}
                  step={1}
                />
              </div>
            </div>
          </div>

          <div className={`ar-section ${activeSection === 2 ? "ar-section--visible" : ""}`}>
            <div className="ar-section-header">
              <span className="ar-section-badge">03</span>
              <h2 className="ar-section-title">Terms & Redemption</h2>
            </div>
            <div className="ar-twin-grid">
              <div className="ar-field">
                <label className="ar-label">Terms & Conditions <span className="ar-req">*</span></label>
                <div className="ar-array-list">
                  {formState.terms_and_conditions.map((term, index) => (
                    <div key={index} className="ar-array-row">
                      <input
                        className="ar-input"
                        type="text"
                        value={term}
                        onChange={(e) => handleArrayChange("terms_and_conditions", index, e.target.value)}
                        placeholder={`Term ${index + 1}`}
                      />
                      {formState.terms_and_conditions.length > 1 && (
                        <button
                          type="button"
                          className="ar-icon-btn ar-icon-btn--danger"
                          onClick={() => handleRemoveArrayItem("terms_and_conditions", index)}
                          aria-label="Remove term"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" className="ar-add-btns" onClick={() => handleAddArrayItem("terms_and_conditions")}>
                  <PlusIcon /> Add Term
                </button>
              </div>

              <div className="ar-field">
                <label className="ar-label">How to Redeem <span className="ar-req">*</span></label>
                <div className="ar-array-list">
                  {formState.how_to_redeem.map((step, index) => (
                    <div key={index} className="ar-array-row">
                      <input
                        className="ar-input"
                        type="text"
                        value={step}
                        onChange={(e) => handleArrayChange("how_to_redeem", index, e.target.value)}
                        placeholder={`Step ${index + 1}`}
                      />
                      {formState.how_to_redeem.length > 1 && (
                        <button
                          type="button"
                          className="ar-icon-btn ar-icon-btn--danger"
                          onClick={() => handleRemoveArrayItem("how_to_redeem", index)}
                          aria-label="Remove Step"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" className="ar-add-btns" onClick={() => handleAddArrayItem("how_to_redeem")}>
                  <PlusIcon /> Add Step
                </button>
              </div>
            </div>
          </div>

          <div className={`ar-section ${activeSection === 3 ? "ar-section--visible" : ""}`}>
            <div className="ar-section-header">
              <span className="ar-section-badge">04</span>
              <h2 className="ar-section-title">Media</h2>
            </div>
            <div className="ar-grid ar-grid-1">
              <div className="ar-field">
                <label className="ar-label">Voucher Image <span className="ar-req">*</span></label>
                <label className="ar-upload-area" htmlFor="image_input">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="ar-img-preview" />
                  ) : (
                    <div className="ar-upload-placeholder">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>{uploading.image ? "Uploading…" : "Click to upload PNG, JPG, JPEG"}</span>
                    </div>
                  )}
                </label>
                <input id="image_input" type="file" name="image" accept="image/png,image/jpeg,image/jpg" onChange={handleFileChange} ref={imageInputRef} className="ar-file-hidden" disabled={uploading.image} />
                {imagePreview && (
                  <button type="button" className="ar-remove-file" onClick={() => { setImagePreview(null); setFormState((p) => ({ ...p, image: "" })); if (imageInputRef.current) imageInputRef.current.value = ""; }}>
                    Remove image
                  </button>
                )}
              </div>

              <div className="ar-field">
                <label className="ar-label">Brochure <span className="ar-optional">(PDF only, optional)</span></label>
                <label className="ar-upload-area ar-upload-area--pdf" htmlFor="brochure_input">
                  {formState.brochure ? (
                    <div className="ar-pdf-uploaded">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span>Brochure uploaded — <a href={formState.brochure} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>View PDF</a></span>
                    </div>
                  ) : (
                    <div className="ar-upload-placeholder">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="12" y1="18" x2="12" y2="12" />
                        <line x1="9" y1="15" x2="15" y2="15" />
                      </svg>
                      <span>{uploading.brochure ? "Uploading…" : "Click to upload PDF brochure"}</span>
                    </div>
                  )}
                </label>
                <input id="brochure_input" type="file" name="brochure" accept="application/pdf" onChange={handleBrochureChange} ref={brochureInputRef} className="ar-file-hidden" disabled={uploading.brochure} />
                {formState.brochure && (
                  <button type="button" className="ar-remove-file" onClick={() => { setFormState((p) => ({ ...p, brochure: "" })); if (brochureInputRef.current) brochureInputRef.current.value = ""; }}>
                    Remove brochure
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="ar-footer">
            <div className="ar-footer-nav">
              <button
                type="button"
                className="ar-btn ar-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                Back
              </button>
              {activeSection < sections.length - 1 ? (
                <button type="button" className="ar-btn ar-btn--primary" onClick={handleNextSection}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="ar-btn ar-btn--submit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                  Save Voucher
                </button>
              )}
            </div>
            <Link to="/rewards" className="ar-btn ar-btn--cancel" onClick={handleReset}>Cancel</Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddRewards;